//#region Imports Packages and Libs

import { Util } from "../util";

//#endregion

//#region String Utilities

/**
 * Utility class for string manipulation operations
 */
export class StringUtils {
	//#region Text Formatting

	/**
	 * Truncates a message to a specified length with ellipsis
	 * @param {string} message - The original message
	 * @param {number} maxLength - Maximum allowed length
	 * @returns {string} - Truncated message
	 */
	public static truncateMessage(message: string, maxLength: number): string {
		try {
			if (!message) return "";

			// Remove any HTML tags that might be in the error
			message = message.replace(/<[^>]*>/g, "");

			// Trim whitespace and normalize newlines
			message = message.replace(/\s+/g, " ").trim();

			// Truncate if the message is too long
			if (message.length > maxLength) {
				return message.substring(0, maxLength) + "...";
			}

			return message;
		} catch (error) {
			// In case of any error during truncation, return a safe fallback
			// console.error("[StringUtils.truncateMessage] Error truncating message:", error);
			return "Message processing error";
		}
	}

	/**
	 * Normalizes a string by trimming whitespace and converting to lowercase.
	 * @param str - The input string to normalize
	 * @returns The normalized string in lowercase with trimmed whitespace,
	 *          or an empty string if the input is falsy or an error occurs
	 */
	public static normalizeString(str: string): string {
		try {
			const trimmed = Util.trimString(str);
			return trimmed ? trimmed.toLowerCase() : "";
		} catch (ex) {
			return ""; // Return an empty string as a fallback
		}
	}

	/**
	 * Determines whether a string is null, undefined, empty, or contains only whitespace characters.
	 * @param value - The string value to check. Can be string, null, or undefined.
	 * @returns True if the value is null, undefined, empty, or contains only whitespace; otherwise, false.
	 */
	public static isNullOrWhitespace(value: unknown): boolean {
		return !value || typeof value !== "string" || Util.isNullOrEmpty(value) || Util.trimString(value) === "";
	}

	//#endregion

	//#region Object Operations

	/**
	 * Safely gets a property value from an object with a default fallback
	 * @template T - The type of the value to return
	 * @param {any} obj - The source object to get property from
	 * @param {string} propName - The property name to access
	 * @param {T} defaultValue - Default value to return if property is null/undefined
	 * @returns {T} - The property value or default value
	 */
	public static getStoreValue<T>(obj: any, propName: string, defaultValue: T): T {
		try {
			// Check if object and property path exist
			if (obj && obj[propName] !== undefined && obj[propName] !== null) {
				return obj[propName] as T;
			}
			return defaultValue;
		} catch (error) {
			// Return default value in case of any error
			return defaultValue;
		}
	}

	//#endregion

	//#region Array Operations

	/**
	 * Joins an array of strings with a specified separator
	 * @param {string[]} items - Array of strings to join
	 * @param {string} separator - Separator to use between items (default: comma+space)
	 * @returns {string} - Joined string
	 */
	public static joinStrings(items: string[], separator: string = ", "): string {
		if (!Array.isArray(items)) return "";
		return items.filter((item) => !!item).join(separator);
	}

	//#endregion
}

//#endregion
